/**
 * Arizona chapter media + lockup copy.
 *
 * Local sources live under `assets/arizona/` (`*.orig.*` kept out of the sync).
 * Re-sync public encodes with:
 *   aws --profile voxmedia s3 sync \
 *     apps/duet-csk-theverge/src/stories/data-centers-hub/assets/arizona/ \
 *     s3://assets.sbnation.com/csk/uploads/verge-features/data-centers/data-centers-hub/arizona/ \
 *     --acl public-read --exclude "*" \
 *     --include "*.webp" --include "*.mp4" --include "*.svg" \
 *     --exclude "*.orig.*"
 */

import { hubS3Asset } from "@story/data-centers-hub/config/hubS3";
import type { TitleMedia } from "@story/data-centers-hub/config/titleAssets";

/** Bust browser/S3 caches after replacing files at the same keys. */
const V = "20261027a";

export function hubArizonaAsset(path: string): string {
  return hubS3Asset("arizona", path, V);
}

/** Arizona cyan — package nav + chapter chrome once the wipe is open. */
export const ARIZONA_CYAN = "#8EEAEA";

/** WP node for the Arizona feature — share / comment / lockup meta. */
export const ARIZONA_FEATURE_NODE_ID = "dmcyOmN1c3RvbVN0b3J5OjEwMTY2NTA=";

/**
 * Lockup fallbacks when the Arizona WP node is missing. Live values come from
 * `authors` / `additionalBylines` / `dek.plaintext` via `resolveLockupCopy`.
 */
export const arizonaCopy = {
  bylineLabel: "by",
  bylineName: "Gaby Del Valle",
  photoBylineLabel: "photos by",
  photoBylineName: "Stephen Voss",
  lede: "The new land rush is for data center-ready property, with power, permits, and zoning all set up for waiting developers. But sometimes, in the rush to make these deals, details get skipped — like the will of ordinary Arizonans.",
  hedAlt: "AI Has Come for Arizona's Water",
  shareTitle: "AI Has Come for Arizona's Water",
} as const;

/** Feature path for share / comments when `featureHref` is set on the slide. */
export const ARIZONA_SHARE_FALLBACK_URL =
  "/cs/features/1016650/arizona-water-data-center-tsmc";

export const arizonaSlideMedia = {
  /** Full-stage underlay — data center construction at sunset. */
  main: {
    kind: "video",
    src: hubArizonaAsset("main.mp4"),
    poster: hubArizonaAsset("main-poster.webp"),
    srcMobile: hubArizonaAsset("main-mobile.mp4"),
    posterMobile: hubArizonaAsset("main-mobile-poster.webp"),
  },
  /** Desktop lockup — wide stage hed. */
  hed: hubArizonaAsset("hed.svg"),
  /** Narrow lockup — stacked lines for the mobile chapter. */
  hedMobile: hubArizonaAsset("hed-mobile.svg"),
  /** Bottom-right — Central Arizona Project irrigation canal. */
  irrigation: {
    kind: "video",
    src: hubArizonaAsset("irrigation.mp4"),
    poster: hubArizonaAsset("irrigation-poster.webp"),
    srcMobile: hubArizonaAsset("irrigation-mobile.mp4"),
    posterMobile: hubArizonaAsset("irrigation-mobile-poster.webp"),
  },
  /** Top-left still — saguaro / desert flora. */
  tl: {
    kind: "image",
    src: hubArizonaAsset("tl.webp"),
  },
  /** Bottom-left still — residents. */
  bl: {
    kind: "image",
    src: hubArizonaAsset("bl.webp"),
  },
} as const satisfies {
  main: TitleMedia;
  hed: string;
  hedMobile: string;
  irrigation: TitleMedia;
  tl: TitleMedia;
  bl: TitleMedia;
};
